import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, MenuItem, Stack, Typography } from "@mui/material";
import { useSnackbar } from "src/components/snackbar";
import FormProvider, { RHFTextField, RHFSelect, RHFDatePicker, RHFAutocomplete } from "src/components/hook-form";
import { useLocales } from "src/locales";
import { format } from "date-fns";
import PhoneNumberInput from "src/components/phone-input";
import SkeletonKycBusinessInfo from "src/components/skeleton/SkeletonKycBusinessInfo";
import { kycApi } from "src/services/kycApi";

export default function BusinessDetailsSimplified({ handleNext, handleBack, handleReset, uniqueId, uniqueIdStatus }) {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [formLoading, setFormLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);

  // Validation schema - only Buckzy required fields
  const validationSchema = Yup.object().shape({
    legalName: Yup.string().required("Legal business name is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    emailAddress: Yup.string().email("Invalid email").required("Email is required"),
    mobileNumber: Yup.string()
      .required("Mobile number is required")
      .min(10, "Phone number must be at least 10 digits (including country code)")
      .matches(/^[0-9]+$/, "Phone number must contain only digits"),
    addressLine1: Yup.string().required("Address is required"),
    city: Yup.mixed().required("City is required"),
    province: Yup.mixed().required("State is required"),
    country: Yup.mixed().required("Country is required"),
    postalCode: Yup.string().required("Postal code is required"),
    taxId: Yup.string().required("Tax ID is required"),
    registrationNumber: Yup.string().required("Registration number is required"),
    establishmentDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        // Return null for empty strings or invalid dates
        if (!originalValue || originalValue === "") {
          return null;
        }
        // If it's already a Date object, return it
        if (originalValue instanceof Date) {
          return isNaN(originalValue.getTime()) ? null : originalValue;
        }
        // Try to parse as date
        const date = new Date(originalValue);
        return isNaN(date.getTime()) ? null : date;
      })
      .typeError("Please select a valid establishment date")
      .required("Establishment date is required")
      .max(new Date(), "Date cannot be in future"),
    lineOfBusiness: Yup.mixed().required("Business category is required"),
    organizationType: Yup.mixed().required("Organization type is required"),
  });

  const defaultValues = {
    legalName: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    mobileNumber: "",
    addressLine1: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    taxId: "",
    registrationNumber: "",
    establishmentDate: "",
    lineOfBusiness: "",
    organizationType: "",
  };

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, setValue, watch, formState: { isSubmitting } } = methods;

  const _country = watch("country");
  const _province = watch("province");

  // Fetch initial data and load from API if uniqueId exists
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch countries
        const countriesRes = await kycApi.getCountries();
        console.log("Countries response:", countriesRes.data);

        // Handle different response structures
        let countriesData = countriesRes.data.data || countriesRes.data || [];
        setCountries(countriesData);

        // Fetch organization types
        const typesRes = await kycApi.getOrganizationTypes();
        let typesData = typesRes.data.data || typesRes.data || [];
        setBusinessTypes(typesData);

        // Fetch line of business
        const categoriesRes = await kycApi.getLineOfBusiness();
        let categoriesData = categoriesRes.data.data || categoriesRes.data || [];
        setBusinessCategories(categoriesData);

        // Fetch existing KYC data if uniqueId exists (will fetch when status is 200 or when navigating back)
        if (uniqueId) {
          try {
            console.log("Fetching existing KYC data for unique_key:", uniqueId);
            const kycDataRes = await kycApi.getKycData(uniqueId);
            const kycData = kycDataRes.data.data || kycDataRes.data;
            
            if (kycData) {
              console.log("Loading KYC data from API:", kycData);

              // Populate form fields
              if (kycData.legalName) setValue("legalName", kycData.legalName);
              if (kycData.firstName) setValue("firstName", kycData.firstName);
              if (kycData.lastName) setValue("lastName", kycData.lastName);
              if (kycData.emailAddress) setValue("emailAddress", kycData.emailAddress);
              if (kycData.taxId) setValue("taxId", kycData.taxId);
              if (kycData.registrationNumber) setValue("registrationNumber", kycData.registrationNumber);
              if (kycData.addressLine1) setValue("addressLine1", kycData.addressLine1);
              if (kycData.postalCode) setValue("postalCode", kycData.postalCode);
              
              // Handle date
              if (kycData.establishmentDate) {
                const date = new Date(kycData.establishmentDate);
                if (!isNaN(date.getTime())) {
                  setValue("establishmentDate", date);
                }
              }

              // Handle phone number
              if (kycData.mobileCountryCode && kycData.mobileNumber) {
                const fullNumber = `${kycData.mobileCountryCode}${kycData.mobileNumber}`;
                setValue("mobileNumber", fullNumber);
              }

              // Find and set country object
              if (kycData.country && countriesData.length > 0) {
                const countryId = kycData.countryOfRegistration || kycData.countryOfOperation || kycData.country;
                const matchingCountry = countriesData.find(c => 
                  c.id?.toString() === countryId?.toString() ||
                  c.code === countryId ||
                  c.name === countryId || 
                  c.countryName === countryId
                );
                if (matchingCountry) {
                  setValue("country", matchingCountry);
                  
                  // Fetch and set province if country is set
                  if (kycData.province) {
                    const countryIdForState = matchingCountry.id || matchingCountry.countryId;
                    if (countryIdForState) {
                      try {
                        const statesRes = await kycApi.getStates(countryIdForState);
                        const statesData = statesRes.data.data || statesRes.data || [];
                        setProvinces(statesData);
                        
                        const provinceId = kycData.province;
                        const matchingProvince = statesData.find(s =>
                          s.id?.toString() === provinceId?.toString() ||
                          s.code === provinceId ||
                          s.name === provinceId ||
                          s.stateName === provinceId
                        );
                        if (matchingProvince) {
                          setValue("province", matchingProvince);
                          
                          // Fetch and set city if province is set
                          if (kycData.city) {
                            const stateId = matchingProvince.id || matchingProvince.stateId;
                            if (stateId) {
                              try {
                                const citiesRes = await kycApi.getCities(countryIdForState, stateId);
                                const citiesData = citiesRes.data.data || citiesRes.data || [];
                                setCities(citiesData);
                                
                                const cityId = kycData.city;
                                const matchingCity = citiesData.find(c =>
                                  c.id?.toString() === cityId?.toString() ||
                                  c.code === cityId ||
                                  c.name === cityId ||
                                  c.cityName === cityId
                                );
                                if (matchingCity) {
                                  setValue("city", matchingCity);
                                }
                              } catch (err) {
                                console.error("Error fetching cities:", err);
                              }
                            }
                          }
                        }
                      } catch (err) {
                        console.error("Error fetching states:", err);
                      }
                    }
                  }
                }
              }

              // Find and set organization type
              if (kycData.organizationType && typesData.length > 0) {
                const orgTypeId = kycData.organizationType;
                const matchingType = typesData.find(t =>
                  t.id?.toString() === orgTypeId?.toString() ||
                  t.code === orgTypeId ||
                  t.name === orgTypeId ||
                  t.description === orgTypeId
                );
                if (matchingType) {
                  setValue("organizationType", matchingType);
                  console.log("✅ Matched organization type:", matchingType);
                } else {
                  console.warn("Could not match organization type:", orgTypeId, "Available types:", typesData);
                }
              }

              // Find and set line of business
              if (kycData.lineOfBusiness && categoriesData.length > 0) {
                const lobId = kycData.lineOfBusiness;
                const matchingCategory = categoriesData.find(c =>
                  c.id?.toString() === lobId?.toString() ||
                  c.code === lobId ||
                  c.name === lobId ||
                  c.description === lobId
                );
                if (matchingCategory) {
                  setValue("lineOfBusiness", matchingCategory);
                  console.log("✅ Matched line of business:", matchingCategory);
                } else {
                  console.warn("Could not match line of business:", lobId, "Available categories:", categoriesData);
                }
              }

              console.log("✅ Form populated from API");
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              console.log("No existing KYC data found - starting fresh");
            } else {
              console.error("Error fetching KYC data:", error);
              enqueueSnackbar("Failed to load existing data", { variant: "warning" });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        enqueueSnackbar("Failed to load form data", { variant: "error" });
      } finally {
        setFormLoading(false);
      }
    };

    fetchData();
  }, [setValue, uniqueId, uniqueIdStatus, enqueueSnackbar]);

  // Fetch provinces when country changes (cascading dropdown logic)
  useEffect(() => {
    // Clear dependent fields first
    setValue("province", null);
    setValue("city", null);
    setProvinces([]);
    setCities([]);

    if (!_country) {
      return;
    }

    const fetchProvinces = async () => {
      try {
        // Extract country ID - try different possible field names
        const countryId = _country?.id || _country?.countryId;

        if (!countryId) {
          console.error("Invalid country ID:", _country);
          return;
        }

        const res = await kycApi.getStates(countryId);
        const statesData = res.data.data || res.data || [];
        setProvinces(statesData);
      } catch (error) {
        console.error("Error fetching provinces:", error.response?.data || error.message);
        enqueueSnackbar("Failed to load states", { variant: "error" });
      }
    };

    fetchProvinces();
  }, [_country, setValue, enqueueSnackbar]);

  // Fetch cities when province changes (cascading dropdown logic)
  useEffect(() => {
    // Clear city first
    setValue("city", null);
    setCities([]);

    if (!_province || !_country) {
      return;
    }

    const fetchCities = async () => {
      try {
        // Extract IDs - try different possible field names
        const countryId = _country?.id || _country?.countryId;
        const stateId = _province?.id || _province?.stateId;

        if (!countryId || !stateId) {
          console.error("Invalid IDs:", { countryId, stateId });
          return;
        }

        const res = await kycApi.getCities(countryId, stateId);
        const citiesData = res.data.data || res.data || [];
        setCities(citiesData);
      } catch (error) {
        console.error("Error fetching cities:", error.response?.data || error.message);
        enqueueSnackbar("Failed to load cities", { variant: "error" });
      }
    };

    fetchCities();
  }, [_province, _country, setValue, enqueueSnackbar]);

  const onSubmit = async (data) => {
    try {
      // Parse phone number to extract country code and number
      const parsePhoneNumber = (fullNumber) => {
        if (!fullNumber) return { countryCode: "1", number: "" };

        const phoneStr = fullNumber.toString();

        // Common country codes (ordered by length, longest first to avoid partial matches)
        const countryCodes = [
          { code: '1', length: 1 },      // US, Canada
          { code: '7', length: 1 },      // Russia, Kazakhstan
          { code: '20', length: 2 },     // Egypt
          { code: '27', length: 2 },     // South Africa
          { code: '30', length: 2 },     // Greece
          { code: '31', length: 2 },     // Netherlands
          { code: '32', length: 2 },     // Belgium
          { code: '33', length: 2 },     // France
          { code: '34', length: 2 },     // Spain
          { code: '36', length: 2 },     // Hungary
          { code: '39', length: 2 },     // Italy
          { code: '40', length: 2 },     // Romania
          { code: '41', length: 2 },     // Switzerland
          { code: '43', length: 2 },     // Austria
          { code: '44', length: 2 },     // UK
          { code: '45', length: 2 },     // Denmark
          { code: '46', length: 2 },     // Sweden
          { code: '47', length: 2 },     // Norway
          { code: '48', length: 2 },     // Poland
          { code: '49', length: 2 },     // Germany
          { code: '51', length: 2 },     // Peru
          { code: '52', length: 2 },     // Mexico
          { code: '53', length: 2 },     // Cuba
          { code: '54', length: 2 },     // Argentina
          { code: '55', length: 2 },     // Brazil
          { code: '56', length: 2 },     // Chile
          { code: '57', length: 2 },     // Colombia
          { code: '58', length: 2 },     // Venezuela
          { code: '60', length: 2 },     // Malaysia
          { code: '61', length: 2 },     // Australia
          { code: '62', length: 2 },     // Indonesia
          { code: '63', length: 2 },     // Philippines
          { code: '64', length: 2 },     // New Zealand
          { code: '65', length: 2 },     // Singapore
          { code: '66', length: 2 },     // Thailand
          { code: '81', length: 2 },     // Japan
          { code: '82', length: 2 },     // South Korea
          { code: '84', length: 2 },     // Vietnam
          { code: '86', length: 2 },     // China
          { code: '90', length: 2 },     // Turkey
          { code: '91', length: 2 },     // India
          { code: '92', length: 2 },     // Pakistan
          { code: '93', length: 2 },     // Afghanistan
          { code: '94', length: 2 },     // Sri Lanka
          { code: '95', length: 2 },     // Myanmar
          { code: '98', length: 2 },     // Iran
          { code: '212', length: 3 },    // Morocco
          { code: '213', length: 3 },    // Algeria
          { code: '216', length: 3 },    // Tunisia
          { code: '218', length: 3 },    // Libya
          { code: '220', length: 3 },    // Gambia
          { code: '221', length: 3 },    // Senegal
          { code: '222', length: 3 },    // Mauritania
          { code: '223', length: 3 },    // Mali
          { code: '224', length: 3 },    // Guinea
          { code: '225', length: 3 },    // Ivory Coast
          { code: '226', length: 3 },    // Burkina Faso
          { code: '227', length: 3 },    // Niger
          { code: '228', length: 3 },    // Togo
          { code: '229', length: 3 },    // Benin
          { code: '230', length: 3 },    // Mauritius
          { code: '231', length: 3 },    // Liberia
          { code: '232', length: 3 },    // Sierra Leone
          { code: '233', length: 3 },    // Ghana
          { code: '234', length: 3 },    // Nigeria
          { code: '235', length: 3 },    // Chad
          { code: '236', length: 3 },    // Central African Republic
          { code: '237', length: 3 },    // Cameroon
          { code: '238', length: 3 },    // Cape Verde
          { code: '239', length: 3 },    // São Tomé and Príncipe
          { code: '240', length: 3 },    // Equatorial Guinea
          { code: '241', length: 3 },    // Gabon
          { code: '242', length: 3 },    // Republic of the Congo
          { code: '243', length: 3 },    // Democratic Republic of the Congo
          { code: '244', length: 3 },    // Angola
          { code: '245', length: 3 },    // Guinea-Bissau
          { code: '246', length: 3 },    // British Indian Ocean Territory
          { code: '248', length: 3 },    // Seychelles
          { code: '249', length: 3 },    // Sudan
          { code: '250', length: 3 },    // Rwanda
          { code: '251', length: 3 },    // Ethiopia
          { code: '252', length: 3 },    // Somalia
          { code: '253', length: 3 },    // Djibouti
          { code: '254', length: 3 },    // Kenya
          { code: '255', length: 3 },    // Tanzania
          { code: '256', length: 3 },    // Uganda
          { code: '257', length: 3 },    // Burundi
          { code: '258', length: 3 },    // Mozambique
          { code: '260', length: 3 },    // Zambia
          { code: '261', length: 3 },    // Madagascar
          { code: '262', length: 3 },    // Réunion
          { code: '263', length: 3 },    // Zimbabwe
          { code: '264', length: 3 },    // Namibia
          { code: '265', length: 3 },    // Malawi
          { code: '266', length: 3 },    // Lesotho
          { code: '267', length: 3 },    // Botswana
          { code: '268', length: 3 },    // Eswatini
          { code: '269', length: 3 },    // Comoros
          { code: '290', length: 3 },    // Saint Helena
          { code: '291', length: 3 },    // Eritrea
          { code: '297', length: 3 },    // Aruba
          { code: '298', length: 3 },    // Faroe Islands
          { code: '299', length: 3 },    // Greenland
          { code: '350', length: 3 },    // Gibraltar
          { code: '351', length: 3 },    // Portugal
          { code: '352', length: 3 },    // Luxembourg
          { code: '353', length: 3 },    // Ireland
          { code: '354', length: 3 },    // Iceland
          { code: '355', length: 3 },    // Albania
          { code: '356', length: 3 },    // Malta
          { code: '357', length: 3 },    // Cyprus
          { code: '358', length: 3 },    // Finland
          { code: '359', length: 3 },    // Bulgaria
          { code: '370', length: 3 },    // Lithuania
          { code: '371', length: 3 },    // Latvia
          { code: '372', length: 3 },    // Estonia
          { code: '373', length: 3 },    // Moldova
          { code: '374', length: 3 },    // Armenia
          { code: '375', length: 3 },    // Belarus
          { code: '376', length: 3 },    // Andorra
          { code: '377', length: 3 },    // Monaco
          { code: '378', length: 3 },    // San Marino
          { code: '380', length: 3 },    // Ukraine
          { code: '381', length: 3 },    // Serbia
          { code: '382', length: 3 },    // Montenegro
          { code: '383', length: 3 },    // Kosovo
          { code: '385', length: 3 },    // Croatia
          { code: '386', length: 3 },    // Slovenia
          { code: '387', length: 3 },    // Bosnia and Herzegovina
          { code: '389', length: 3 },    // North Macedonia
          { code: '420', length: 3 },    // Czech Republic
          { code: '421', length: 3 },    // Slovakia
          { code: '423', length: 3 },    // Liechtenstein
          { code: '500', length: 3 },    // Falkland Islands
          { code: '501', length: 3 },    // Belize
          { code: '502', length: 3 },    // Guatemala
          { code: '503', length: 3 },    // El Salvador
          { code: '504', length: 3 },    // Honduras
          { code: '505', length: 3 },    // Nicaragua
          { code: '506', length: 3 },    // Costa Rica
          { code: '507', length: 3 },    // Panama
          { code: '508', length: 3 },    // Saint Pierre and Miquelon
          { code: '509', length: 3 },    // Haiti
          { code: '590', length: 3 },    // Guadeloupe
          { code: '591', length: 3 },    // Bolivia
          { code: '592', length: 3 },    // Guyana
          { code: '593', length: 3 },    // Ecuador
          { code: '594', length: 3 },    // French Guiana
          { code: '595', length: 3 },    // Paraguay
          { code: '596', length: 3 },    // Martinique
          { code: '597', length: 3 },    // Suriname
          { code: '598', length: 3 },    // Uruguay
          { code: '599', length: 3 },    // Netherlands Antilles
          { code: '670', length: 3 },    // East Timor
          { code: '672', length: 3 },    // Norfolk Island
          { code: '673', length: 3 },    // Brunei
          { code: '674', length: 3 },    // Nauru
          { code: '675', length: 3 },    // Papua New Guinea
          { code: '676', length: 3 },    // Tonga
          { code: '677', length: 3 },    // Solomon Islands
          { code: '678', length: 3 },    // Vanuatu
          { code: '679', length: 3 },    // Fiji
          { code: '680', length: 3 },    // Palau
          { code: '681', length: 3 },    // Wallis and Futuna
          { code: '682', length: 3 },    // Cook Islands
          { code: '683', length: 3 },    // Niue
          { code: '685', length: 3 },    // Samoa
          { code: '686', length: 3 },    // Kiribati
          { code: '687', length: 3 },    // New Caledonia
          { code: '688', length: 3 },    // Tuvalu
          { code: '689', length: 3 },    // French Polynesia
          { code: '690', length: 3 },    // Tokelau
          { code: '691', length: 3 },    // Micronesia
          { code: '692', length: 3 },    // Marshall Islands
          { code: '850', length: 3 },    // North Korea
          { code: '852', length: 3 },    // Hong Kong
          { code: '853', length: 3 },    // Macau
          { code: '855', length: 3 },    // Cambodia
          { code: '856', length: 3 },    // Laos
          { code: '880', length: 3 },    // Bangladesh
          { code: '886', length: 3 },    // Taiwan
          { code: '960', length: 3 },    // Maldives
          { code: '961', length: 3 },    // Lebanon
          { code: '962', length: 3 },    // Jordan
          { code: '963', length: 3 },    // Syria
          { code: '964', length: 3 },    // Iraq
          { code: '965', length: 3 },    // Kuwait
          { code: '966', length: 3 },    // Saudi Arabia
          { code: '967', length: 3 },    // Yemen
          { code: '968', length: 3 },    // Oman
          { code: '970', length: 3 },    // Palestine
          { code: '971', length: 3 },    // UAE
          { code: '972', length: 3 },    // Israel
          { code: '973', length: 3 },    // Bahrain
          { code: '974', length: 3 },    // Qatar
          { code: '975', length: 3 },    // Bhutan
          { code: '976', length: 3 },    // Mongolia
          { code: '977', length: 3 },    // Nepal
          { code: '992', length: 3 },    // Tajikistan
          { code: '993', length: 3 },    // Turkmenistan
          { code: '994', length: 3 },    // Azerbaijan
          { code: '995', length: 3 },    // Georgia
          { code: '996', length: 3 },    // Kyrgyzstan
          { code: '998', length: 3 },    // Uzbekistan
        ];

        // Try to match country codes (longest first to avoid partial matches)
        for (const cc of countryCodes) {
          if (phoneStr.startsWith(cc.code)) {
            const number = phoneStr.substring(cc.length);
            // Validate that remaining part looks like a valid phone number
            if (number.length >= 7) {  // Minimum phone number length
              return {
                countryCode: cc.code,
                number: number
              };
            }
          }
        }

        // If no match and number is long enough, assume it might be a US number without country code
        if (phoneStr.length >= 10 && phoneStr.length <= 11) {
          return { countryCode: "1", number: phoneStr };
        }

        // Default fallback
        return { countryCode: "1", number: phoneStr };
      };

      const { countryCode, number } = parsePhoneNumber(data.mobileNumber);

      console.log("Parsed phone number:", { original: data.mobileNumber, countryCode, number });

      // Validate phone number after parsing
      if (!number || number.length < 7) {
        enqueueSnackbar(
          "Please enter a complete phone number (at least 7 digits after country code)",
          { variant: "error" }
        );
        return;
      }

      // Helper to extract code/value from option
      const getCode = (option) => option?.code || option?.id || option;
      // Helper to extract ID from option
      const getId = (option) => option?.id || option?.code || option;
      const getStateId = (option) => option?.id || option?.stateId || option?.code || option;
      const getCityId = (option) => option?.id || option?.cityId || option?.code || option;
      // Helper to extract display name from option
      const getName = (option) => option?.name || option?.countryName || option?.stateName || option?.cityName || option?.description || option?.code || option || "";

      // Payload for API submission (with IDs and unique_key)
      const payload = {
        unique_key: uniqueId || "",
        consent: "Yes",
        tp: "B", // Business
        side: "Creditor",
        legalName: data.legalName,
        firstName: data.firstName,
        lastName: data.lastName,
        taxId: data.taxId,
        registrationNumber: data.registrationNumber,
        establishmentDate: format(data.establishmentDate, "yyyy-MM-dd"),
        lineOfBusiness: getCode(data.lineOfBusiness),
        organizationType: getCode(data.organizationType),
        countryOfRegistration: getId(data.country),
        countryOfOperation: getId(data.country),
        addressLine1: data.addressLine1,
        city: getCityId(data.city), // Extract ID from city object
        province: getStateId(data.province), // Extract ID from province object
        country: getId(data.country),
        postalCode: data.postalCode,
        mobileCountryCode: countryCode,
        mobileNumber: number,
        emailAddress: data.emailAddress,
      };

      console.log("Submitting business details payload:", payload);

      // Submit business details to API (PATCH - creates or updates)
      const response = await kycApi.submitKycBusinessData(payload);
      const statusCode = response?.status || response?.response?.status;

      if (statusCode === 201) {
        enqueueSnackbar("Business details created successfully!", { variant: "success" });
      } else if (statusCode === 200) {
        enqueueSnackbar("Business details updated successfully!", { variant: "success" });
      } else {
        enqueueSnackbar("Business details saved successfully!", { variant: "success" });
      }

      handleNext();
    } catch (error) {
      console.error("Submit error:", error);
      enqueueSnackbar(error?.response?.data?.message || "Failed to save business details", {
        variant: "error",
      });
    }
  };

  if (formLoading) {
    return <SkeletonKycBusinessInfo />;
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {t("businessDetails")}
        </Typography>

        <Box
          rowGap={3}
          columnGap={2}
          display="grid"
          gridTemplateColumns={{ xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)" }}
        >
          <RHFTextField name="legalName" label={"Legal Business Name"} />

          <RHFTextField name="firstName" label={t("firstName")} />

          <RHFTextField name="lastName" label={t("lastName")} />

          <RHFTextField name="emailAddress" label={t("email")} />

          <PhoneNumberInput name="mobileNumber" label={"Mobile Number"} />

          <RHFAutocomplete
            name="lineOfBusiness"
            label={t("businessCategory")}
            options={businessCategories}
            getOptionLabel={(option) => option?.name || option?.description || ""}
            isOptionEqualToValue={(option, value) => {
              const optionId = option?.id || option?.code;
              const valueId = value?.id || value?.code;
              return optionId === valueId;
            }}
          />

          <RHFAutocomplete
            name="organizationType"
            label={"Organization Type"}
            options={businessTypes}
            getOptionLabel={(option) => option?.name || option?.description || ""}
            isOptionEqualToValue={(option, value) => {
              const optionId = option?.id || option?.code;
              const valueId = value?.id || value?.code;
              return optionId === valueId;
            }}
          />

          <RHFTextField name="registrationNumber" label={t("registrationNumber")} />

          <RHFTextField name="taxId" label={t("taxId")} />

          <RHFDatePicker
            name="establishmentDate"
            label={"Establishment Date"}
            maxDate={new Date()}
          />

          <RHFTextField name="addressLine1" label={t("address")} />

          <RHFAutocomplete
            name="country"
            label={t("country")}
            options={countries}
            getOptionLabel={(option) => {
              // Handle different possible field names
              const label = option?.name || option?.countryName || option?.description || option?.code || "";
              return label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionId = option?.id || option?.countryId;
              const valueId = value?.id || value?.countryId;
              return optionId === valueId;
            }}
          />

          <RHFAutocomplete
            name="province"
            label={t("province")}
            options={provinces}
            disabled={!provinces.length || !_country}
            getOptionLabel={(option) => {
              const label = option?.name || option?.stateName || option?.description || option?.code || "";
              return label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionId = option?.id || option?.stateId;
              const valueId = value?.id || value?.stateId;
              return optionId === valueId;
            }}
          />

          <RHFAutocomplete
            name="city"
            label={t("city")}
            options={cities}
            disabled={!cities.length || !_province}
            getOptionLabel={(option) => {
              const label = option?.name || option?.cityName || option?.description || "";
              return label;
            }}
            isOptionEqualToValue={(option, value) => {
              const optionId = option?.id || option?.cityId;
              const valueId = value?.id || value?.cityId;
              return optionId === valueId;
            }}
          />

          <RHFTextField name="postalCode" label={t("postalCode")} />
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button onClick={handleBack} disabled={isSubmitting}>
            {t("back")}
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {t("continue")}
          </LoadingButton>
        </Stack>
      </Card>
    </FormProvider>
  );
}
